// ABOUTME: Standalone status screen — title with a technical code, explanation, actions, support line.
// ABOUTME: One frame for 404 and every other "this page has nothing but a message" screen.

import type { FC, ReactNode } from "react"
import { CButton } from "./CButton"
import { cn } from "@shared/lib/utils"

export interface CStatusScreenAction {
  label: string
  onClick: () => void
}

export interface CStatusScreenProps {
  title: string
  /** Technical code next to the title — "404". Monospaced and faint: it is for support, not the user. */
  code?: string
  description?: ReactNode
  primaryAction?: CStatusScreenAction
  secondaryAction?: CStatusScreenAction
  /** Line under the hairline rule — typically the support link. */
  footer?: ReactNode
  className?: string
}

// Standalone screens run full-width actions at the 44px `touch` height (docs/DESIGN_SYSTEM.md §1).
const ACTION_CLASS = "w-full"

/**
 * CStatusScreen — the cabinet variant of the design's status frame.
 *
 * Desktop: light backdrop, white 400px card near the top. Below `sm` the card drops its frame
 * (no radius, shadow or side padding) and the page turns white, per the mobile rule in
 * docs/DESIGN_SYSTEM.md §4.1.
 */
export const CStatusScreen: FC<CStatusScreenProps> = ({
  title,
  code,
  description,
  primaryAction,
  secondaryAction,
  footer,
  className,
}) => (
  <div
    className={cn(
      "flex min-h-full justify-center bg-customGray-100 px-5 py-12",
      "max-sm:bg-white max-sm:pt-10 max-sm:pb-8",
      className,
    )}
  >
    <div
      className={cn(
        "flex h-fit w-full max-w-[400px] flex-col gap-4 rounded-xl bg-white px-6 py-8 shadow-cardShadow",
        "max-sm:max-w-none max-sm:rounded-none max-sm:px-0 max-sm:pt-2 max-sm:pb-6 max-sm:shadow-none",
      )}
    >
      {/* The code sits inside the heading rather than beside it: a title that wraps (five
          languages, long words) then keeps the code after its last word instead of stranding
          it on the first line. */}
      <h1 className="text-xl font-bold break-words">
        {title}
        {code && <span className="font-mono text-[13px] font-normal text-gray-400"> {code}</span>}
      </h1>

      {description && <p className="text-sm leading-normal text-pretty text-gray-600">{description}</p>}

      {(primaryAction || secondaryAction) && (
        <div className="flex flex-col gap-2.5">
          {primaryAction && (
            <CButton
              color="blue"
              size="touch"
              className={cn(ACTION_CLASS, "font-semibold hover:bg-primary-700")}
              label={primaryAction.label}
              onClick={primaryAction.onClick}
            />
          )}
          {secondaryAction && (
            <CButton
              color="gray"
              size="touch"
              className={ACTION_CLASS}
              label={secondaryAction.label}
              onClick={secondaryAction.onClick}
            />
          )}
        </div>
      )}

      {footer && (
        <div className="mt-1 flex items-center justify-center gap-1.5 border-t border-border-faint pt-3 text-sm text-muted-foreground">
          {footer}
        </div>
      )}
    </div>
  </div>
)
